import type { GpxTrackPoint } from "./types";

// ── Haversine (metres) ────────────────────────────────────────────────────────

function haversineM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6_371_000;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(Δφ / 2) ** 2 +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// ── Ramer-Douglas-Peucker simplification ─────────────────────────────────────

function perpendicularDistanceDeg(
  pt: GpxTrackPoint,
  start: GpxTrackPoint,
  end: GpxTrackPoint,
): number {
  const dx = end.lng - start.lng;
  const dy = end.lat - start.lat;
  if (dx === 0 && dy === 0) {
    return Math.hypot(pt.lng - start.lng, pt.lat - start.lat);
  }
  const t = ((pt.lng - start.lng) * dx + (pt.lat - start.lat) * dy) / (dx * dx + dy * dy);
  const nearLng = start.lng + t * dx;
  const nearLat = start.lat + t * dy;
  return Math.hypot(pt.lng - nearLng, pt.lat - nearLat);
}

function rdpRecurse(
  pts: GpxTrackPoint[],
  start: number,
  end: number,
  tolerance: number,
  keep: boolean[],
): void {
  if (end <= start + 1) return;
  let maxDist = 0;
  let maxIdx = start;
  for (let i = start + 1; i < end; i++) {
    const d = perpendicularDistanceDeg(pts[i], pts[start], pts[end]);
    if (d > maxDist) { maxDist = d; maxIdx = i; }
  }
  if (maxDist > tolerance) {
    keep[maxIdx] = true;
    rdpRecurse(pts, start, maxIdx, tolerance, keep);
    rdpRecurse(pts, maxIdx, end, tolerance, keep);
  }
}

/**
 * Ramer-Douglas-Peucker simplification in degree units.
 * tolerance ~0.00001 ≈ 1m; ~0.0001 ≈ 10m; ~0.001 ≈ 100m
 */
export function simplifyTrack(pts: GpxTrackPoint[], tolerance: number): GpxTrackPoint[] {
  if (tolerance <= 0 || pts.length <= 2) return pts;
  const keep = new Array<boolean>(pts.length).fill(false);
  keep[0] = true;
  keep[pts.length - 1] = true;
  rdpRecurse(pts, 0, pts.length - 1, tolerance, keep);
  const simplified = pts.filter((_, i) => keep[i]);
  // Recompute distFromStart after simplification
  return recomputeDist(simplified);
}

// ── Recompute cumulative distances ────────────────────────────────────────────

function recomputeDist(pts: GpxTrackPoint[]): GpxTrackPoint[] {
  return pts.map((p, i) => ({
    ...p,
    distFromStart:
      i === 0
        ? 0
        : pts[i - 1].distFromStart + haversineM(pts[i - 1].lat, pts[i - 1].lng, p.lat, p.lng),
  }));
}

// ── Trim track by fractional start/end ───────────────────────────────────────

/**
 * Slice the track to the window [startFrac, endFrac] (both 0–1).
 * Interpolates exact endpoint positions so the trim is seamless.
 */
export function sliceTrack(
  pts: GpxTrackPoint[],
  startFrac: number,
  endFrac: number,
): GpxTrackPoint[] {
  if (pts.length < 2) return pts;
  if (startFrac <= 0 && endFrac >= 1) return pts;

  const total = pts[pts.length - 1].distFromStart;
  const startM = startFrac * total;
  const endM   = endFrac   * total;

  function interpolateAt(targetM: number): GpxTrackPoint {
    for (let i = 1; i < pts.length; i++) {
      if (pts[i].distFromStart >= targetM) {
        const prev = pts[i - 1];
        const curr = pts[i];
        const segLen = curr.distFromStart - prev.distFromStart;
        const t = segLen === 0 ? 0 : (targetM - prev.distFromStart) / segLen;
        return {
          lat: prev.lat + t * (curr.lat - prev.lat),
          lng: prev.lng + t * (curr.lng - prev.lng),
          ele: prev.ele != null && curr.ele != null ? prev.ele + t * (curr.ele - prev.ele) : null,
          time: null,
          distFromStart: targetM,
        };
      }
    }
    return pts[pts.length - 1];
  }

  const startPt = interpolateAt(startM);
  const endPt   = interpolateAt(endM);

  const middle = pts.filter(
    (p) => p.distFromStart > startM && p.distFromStart < endM,
  );

  const sliced = [startPt, ...middle, endPt];
  // Re-zero distFromStart so it starts at 0
  return sliced.map((p) => ({
    ...p,
    distFromStart: p.distFromStart - startM,
  }));
}

// ── Project a point onto the route ───────────────────────────────────────────

/**
 * Returns the fraction [0, 1] along the route where `lat/lng` projects closest.
 * Used to sort proximity-search results by route position.
 */
export function projectPointOntoRoute(
  lat: number,
  lng: number,
  pts: GpxTrackPoint[],
): number {
  if (pts.length < 2) return 0;
  const total = pts[pts.length - 1].distFromStart;
  if (total === 0) return 0;

  let bestDist = Infinity;
  let bestM = 0;

  for (let i = 1; i < pts.length; i++) {
    const A = pts[i - 1];
    const B = pts[i];
    const dx = B.lng - A.lng;
    const dy = B.lat - A.lat;
    const segLen2 = dx * dx + dy * dy;
    let t = 0;
    if (segLen2 > 0) {
      t = Math.max(0, Math.min(1, ((lng - A.lng) * dx + (lat - A.lat) * dy) / segLen2));
    }
    const nearLng = A.lng + t * dx;
    const nearLat = A.lat + t * dy;
    const d = haversineM(lat, lng, nearLat, nearLng);
    if (d < bestDist) {
      bestDist = d;
      bestM = A.distFromStart + t * (B.distFromStart - A.distFromStart);
    }
  }
  return bestM / total;
}

// ── Convert track to GeoJSON coordinate array ─────────────────────────────────

/** Returns [lng, lat] pairs in GeoJSON order for MapViewer's routeGeometry prop. */
export function routeToGeojsonCoords(pts: GpxTrackPoint[]): [number, number][] {
  return pts.map((p) => [p.lng, p.lat]);
}
